import { Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { delayWhen, EMPTY, Observable, retryWhen, Subject, switchAll, timer } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { Store } from '@ngrx/store';
import { WebSocketActions } from './web-socket.actions';

@Injectable({
	providedIn: 'root',
})
export class WebSocketService {
	private reopen = true;
	private socket$: WebSocketSubject<any>;
	private messagesSubject$ = new Subject();
	public messages$ = this.messagesSubject$.pipe(
		switchAll(),
		catchError((e) => {
			throw e;
		})
	);

	constructor(private readonly store: Store) {
		this.messages$
			.pipe(
				tap((payload) => {
					if (environment.webSocket.debugMode) {
						console.log('[WebSocket Service] Message received:', payload);
					}
				})
			)
			.subscribe((payload) => this.store.dispatch(WebSocketActions.read(payload)));
	}

	private getNewWebSocket(): WebSocketSubject<any> {
		return webSocket({
			url: environment.webSocket.endpoint,
			closeObserver: {
				next: () => {
					if (environment.webSocket.debugMode) {
						console.log('[WebSocket Service] Connection closed');
					}
					this.socket$ = undefined;

					if (this.reopen) {
						this.connect();
					}
				},
			},
		});
	}

	public connect(): boolean {
		this.reopen = true;
		if (!this.socket$ || this.socket$.closed) {
			this.socket$ = this.getNewWebSocket();
			const messages = this.socket$.pipe(
				this.reconnect,
				tap({
					error: (error) => {
						if (environment.webSocket.debugMode) {
							console.log(error);
						}
					},
				}),
				catchError((_) => EMPTY)
			);
			this.messagesSubject$.next(messages);

			if (environment.webSocket.debugMode) {
				console.log('[WebSocket Service] Connection open');
			}
			this.store.dispatch(WebSocketActions.connect());
			return true;
		}
		return false;
	}

	private reconnect(observable: Observable<any>): Observable<any> {
		return observable.pipe(
			retryWhen((errors) =>
				errors.pipe(
					tap((val) => {
						if (environment.webSocket.debugMode) {
							console.log('[WebSocket Service] Trying to reconnect', val);
						}
					}),
					delayWhen((_) => timer(environment.webSocket.reconnectTimeIntervalSeconds))
				)
			)
		);
	}

	public sendMessage<T extends { action: string }>(message: T): void {
		this.socket$.next(message);
		if (environment.webSocket.debugMode) {
			console.log('[WebSocket Service] Send Message:', message);
		}
	}

	public close(reopen: boolean): void {
		if (!this.socket$) return;

		this.reopen = reopen;
		this.socket$.complete();
	}
}
